/**
 * Date utilities to handle date-only values without timezone conversion issues
 */

/**
 * Format a date string (YYYY-MM-DD) for display without timezone conversion
 * This prevents the common issue where "2024-06-30" becomes "June 29" in certain timezones
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {object} options - Formatting options for toLocaleDateString
 * @returns {string} - Formatted date string
 */
export const formatDateOnly = (dateString, options = { month: 'long', day: 'numeric' }) => {
  if (!dateString) return 'N/A';
  
  try {
    // Parse the date string manually to avoid timezone conversion issues
    const [year, month, day] = dateString.split('-').map(Number);
    
    // Create date object using local timezone (not UTC)
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original on error
  }
};

/**
 * Parse a date string (YYYY-MM-DD) to a Date object using local timezone
 * This prevents timezone conversion issues when working with date-only values
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date|null} - Date object in local timezone, or null if invalid
 */
export const parseDateOnly = (dateString) => {
  if (!dateString) return null;
  
  try {
    const [year, month, day] = dateString.split('-').map(Number);
    
    // Create date object using local timezone (not UTC)
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Format birthday date string for display
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {string} - Formatted birthday string (e.g., "June 30")
 */
export const formatBirthdayDate = (dateString) => {
  return formatDateOnly(dateString, { month: 'long', day: 'numeric' });
};

/**
 * Get birthday timing for comparison purposes (today, past, upcoming)
 * @param {string} birthdayString - Birthday string in YYYY-MM-DD format
 * @returns {string} - 'today', 'past', or 'upcoming'
 */
export const getBirthdayTiming = (birthdayString) => {
  if (!birthdayString) return 'upcoming';
  
  try {
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Parse birthday manually to avoid timezone issues
    const [, month, day] = birthdayString.split('-').map(Number);
    
    // Set birthday to current year for comparison using local timezone
    const birthdayThisYear = new Date(currentYear, month - 1, day); // month is 0-indexed
    
    // Calculate difference in days
    const diffTime = birthdayThisYear.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Check if it's today
    if (diffDays === 0) {
      return 'today';
    }
    
    // Check if it was in the past this year
    if (diffDays < 0) {
      return 'past';
    }
    
    return 'upcoming';
  } catch (error) {
    console.error('Error calculating birthday timing:', error);
    return 'upcoming';
  }
};